import React, { FC, SyntheticEvent } from 'react'
import { AmpcoreMaterialIconId } from '@ampcore/ampcom-contracts'
import { MaterialIcon } from '../MaterialIcon/MaterialIcon'

export type SidePanelHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  prevBtnClassNames?: string
  handlePrevPageBtn?: (event: SyntheticEvent) => void
  heading?: React.ReactNode
  minimizable?: boolean
  isMinimized?: boolean
  toggleMinimized?: (event: SyntheticEvent) => void
  handleClose?: (event: SyntheticEvent) => void
  /**
   * Whether the panel supports previous-page navigation (multi-page). When
   * false, the prev button is not rendered at all, matching the ADEL
   * single-page/children markup so the sticky header lays out correctly.
   */
  showPrevButton?: boolean
  /**
   * Whether a previous page is currently reachable (currentPage > 0). Drives the
   * `-prev` modifier classes that ADEL CSS uses to reveal the prev button.
   */
  isOnPrevPage?: boolean
  testId?: string
}

export const SidePanelHeader: FC<SidePanelHeaderProps> = props => {
  const {
    prevBtnClassNames = '',
    handlePrevPageBtn,
    heading = 'Side Panel Heading',
    minimizable = false,
    isMinimized,
    toggleMinimized,
    handleClose,
    showPrevButton = false,
    isOnPrevPage = false,
    testId
  } = props

  const minimizeTitle = isMinimized
    ? 'Maximize panel'
    : 'Minimize panel to bottom corner'

  const headerClassName =
    'SidePanel-header' + (isOnPrevPage ? ' SidePanel-header-prev' : '')
  const headingClassName =
    'SidePanel-heading' + (isOnPrevPage ? ' SidePanel-heading-prev' : '')

  return (
    <header className="SidePanel-headerWrapper">
      <div className={headerClassName}>
        <div className={headingClassName}>
          {showPrevButton && (
            <button
              className={prevBtnClassNames}
              type="button"
              title="Previous page in panel"
              onClick={handlePrevPageBtn}
            >
              <span className="u-hiddenVisually">Previous page in panel</span>
              <MaterialIcon
                icon={AmpcoreMaterialIconId.ARROW_BACK_IOS}
                className="u-colorBrandIcon u-marginLeftM4"
              />
            </button>
          )}
          {typeof heading === 'string' ? (
            <h2 className="SidePanel-headingContent">{heading}</h2>
          ) : (
            <div className="SidePanel-headingContent">{heading}</div>
          )}
        </div>
        <div className="SidePanel-icons">
          {minimizable && (
            <button
              className="Button Button--icon Button--icon--neutral SidePanel-Button--minimize"
              title={minimizeTitle}
              type="button"
              onClick={toggleMinimized}
              data-testid={testId ? `${testId}-minimize-panel` : undefined}
            >
              <span className="SidePanel-minimizeIcon">
                <span className="u-hiddenVisually">
                  Minimize panel to bottom corner
                </span>

                <MaterialIcon
                  icon={AmpcoreMaterialIconId.COLLAPSE_ALL}
                  className="u-colorBrandIcon u-marginLeftM4"
                />
              </span>
              <span className="SidePanel-maximizeIcon">
                <span className="u-hiddenVisually">Maximize panel</span>

                <MaterialIcon
                  icon={AmpcoreMaterialIconId.EXPAND_ALL}
                  className="u-colorBrandIcon u-marginLeftM4"
                />
              </span>
            </button>
          )}
          <button
            className="Button Button--icon Button--icon--neutral SidePanel-Button--close"
            title="Close panel"
            type="button"
            onClick={handleClose}
            data-testid={testId ? `${testId}-close-panel` : undefined}
          >
            <span className="u-hiddenVisually">Close panel</span>
            <MaterialIcon
              icon={AmpcoreMaterialIconId.CLOSE}
              className="u-colorBrandIcon"
            />
          </button>
        </div>
      </div>
    </header>
  )
}
